<template>
  <div class="suggestion-details" data-testid="suggestion-details">
    <ContentDisplay
      v-if="showTransactionInfos"
      footer-type="caption"
      :footer-label="summaryCaption"
    >
      <template #content-block-1>
        <HeadingDisplay title="Resumo da sugestão" size="small" />
        <ListDisplay
          title="Sugerido em"
          :value="suggestionDetails.transaction?.createdAt?.formatted || ''"
        />
        <ListDisplay
          v-if="showExpireDate"
          title="Expira em"
          :value="suggestionDetails.transaction?.expiredAt?.formatted || ''"
        />
        <ListDisplay
          title="Origem"
          :value="suggestionDetails.transaction?.origin || ''"
        />
        <ListDisplay
          title="Destino"
          :value="suggestionDetails.transaction?.destination || ''"
        />
        <ListDisplay
          :title="amountLabel"
          :value="suggestionDetails.transaction?.amount?.formatted || ''"
        />
        <ListDisplay
          v-if="!isInvestment"
          title="Total líquido aproximado "
          :value="suggestionDetails.transaction?.netAmount?.formatted || ''"
        />
      </template>
    </ContentDisplay>
    <ContentDisplay
      v-if="showPortfolioInfos"
      footer-type="caption"
      :footer-label="portfolioCaption"
    >
      <template #content-block-1>
        <HeadingDisplay title="Características da carteira" size="small" />
        <ListDisplay
          title="Nome da carteira"
          :value="suggestionDetails.portfolio?.name"
        />
        <ListDisplay
          title="Tipo de gestão"
          :value="suggestionDetails.portfolio?.managementType"
        />
        <ListDisplay
          v-if="suggestionDetails.portfolio?.fee"
          title="Custos da carteira"
          :value="suggestionDetails.portfolio?.fee?.formatted"
          :show-icon="true"
          icon="EF0080"
          @click="toggleDynamicModal(suggestionDetails.portfolio?.fee)"
        />
        <div v-if="suggestionDetails.portfolio?.allocations">
          <HeadingDisplay
            class="suggestion-details__card-title"
            title="Alocação"
            size="extra-small"
          />
          <ListDisplay
            v-for="(allocation, index) in suggestionDetails.portfolio
              ?.allocations"
            :key="index"
            :title="allocation.type?.formatted"
            :value="allocation.percentage?.formatted"
          />
        </div>
      </template>
    </ContentDisplay>
    <ContentDisplay
      v-if="showProductsInfos"
      class="suggestion-details__products"
    >
      <template #content-block-1>
        <HeadingDisplay title="Produtos" size="small" />
        <div
          v-for="(product, index) in suggestionDetails.transaction?.products"
          :key="index"
        >
          <HeadingDisplay
            class="suggestion-details__card-title"
            :title="product.productName"
            size="extra-small"
          />
          <ListDisplay
            v-if="!isInvestment"
            title="Resgate"
            :value="product.liquidation"
          />
          <ListDisplay
            :title="productAmountLabel"
            :value="product.amount?.formatted"
          />
          <ListDisplay
            v-if="product.portfolioShare"
            title="Participação na carteira"
            :value="product.portfolioShare"
          />
          <ListDisplay
            v-if="product.rebateCashbackAdministration"
            title="Rebate/ Cashback de administração"
            :value="product.rebateCashbackAdministration?.value"
            :show-icon="true"
            icon="EF0080"
            @click="toggleDynamicModal(product.rebateCashbackAdministration)"
          />
          <ListDisplay
            v-if="product.rebateCashbackPerformance"
            title="Rebate/ Cashback de performance"
            :value="product.rebateCashbackPerformance?.value"
            :show-icon="true"
            icon="EF0080"
            @click="toggleDynamicModal(product.rebateCashbackPerformance)"
          />
          <ListDisplay
            v-if="product.spread"
            title="Remuneração por intermediação"
            :value="product.spread.value"
            :show-icon="true"
            icon="EF0080"
            @click="toggleDynamicModal(product.spread)"
          />
        </div>
      </template>
    </ContentDisplay>
    <ContentDisplay
      v-if="showCustomOperationsInfos"
      footer-type="caption"
      :footer-label="suggestionDetails.athenaProductTransaction?.warning"
    >
      <template #content-block-1>
        <div class="suggestion-details__heading">
          <div>
            <BaseText
              v-if="suggestionDetails.athenaProductTransaction?.subtitle"
              :color="NebraskaColors.elementSecondary"
              size="p5"
            >
              {{ suggestionDetails.athenaProductTransaction?.subtitle }}
            </BaseText>
            <Heading class="review-step__name" :level="6">
              {{ suggestionDetails.athenaProductTransaction?.symbol }}
            </Heading>
          </div>
          <InstrumentAvatar
            v-if="suggestionDetails.athenaProductTransaction?.symbol"
            size="small"
            :symbol="suggestionDetails.athenaProductTransaction?.symbol || ''"
            :alt="suggestionDetails.athenaProductTransaction?.symbol || ''"
          />
        </div>
        <ListDisplay
          title="Quantidade"
          :value="
            suggestionDetails.athenaProductTransaction?.quantity?.toString()
          "
        />
        <ListDisplay
          v-if="
            suggestionDetails.athenaProductTransaction?.operationValue.value
          "
          title="Valor da operação"
          :value="
            suggestionDetails.athenaProductTransaction?.operationValue
              ?.formatted
          "
        />
        <ListDisplay
          title="Vencimento"
          :value="
            suggestionDetails.athenaProductTransaction?.expiredAt?.formatted
          "
        />
        <ListDisplay
          v-if="suggestionDetails.athenaProductTransaction?.brokerageFee"
          title="Taxa de corretagem"
          :value="suggestionDetails.athenaProductTransaction?.brokerageFee"
        />
        <ListDisplay
          v-if="suggestionDetails.athenaProductTransaction?.structuringFee"
          title="Taxa de estruturação"
          :value="suggestionDetails.athenaProductTransaction?.structuringFee"
        />
        <ListDisplay
          v-if="suggestionDetails.athenaProductTransaction?.coordinationFee"
          title="Taxa de coordenação"
          :value="suggestionDetails.athenaProductTransaction?.coordinationFee"
        />
        <ListDisplay
          v-if="suggestionDetails.athenaProductTransaction?.distributionFee"
          title="Taxa de distribuição"
          :value="suggestionDetails.athenaProductTransaction?.distributionFee"
        />
      </template>
    </ContentDisplay>
    <DynamicContentModal
      :is-open="isDynamicContentModalOpen"
      :title="dynamicContentModal.title"
      :modal-info="dynamicContentModal.description"
      :costs-button="dynamicContentModal.showCostsButton"
      @close="toggleDynamicModal"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import {
  BaseText,
  ContentDisplay,
  Heading,
  HeadingDisplay,
  ListDisplay
} from '@warrenbrasil/nebraska-web';
import {
  IGetSuggestionDetailsResponse,
  ProductInformartionWithDisclaimer
} from '@/modules/wealth/services/investment-suggestions';
import * as captions from './constants';
import { IDynamicContent } from '@/modules/common/types';
import DynamicContentModal from '@/modules/common/components/DynamicContentModal/DynamicContentModal.vue';
import InstrumentAvatar from '@/modules/trade/components/InstrumentAvatar/InstrumentAvatar.vue';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';

interface Props {
  suggestionDetails: IGetSuggestionDetailsResponse;
  isInvestment: boolean;
}

const props = defineProps<Props>();

const isDynamicContentModalOpen = ref(false);
const dynamicContentModal = ref({
  title: '',
  description: [] as IDynamicContent[],
  showCostsButton: false
});

const showCustomOperationsInfos = computed(() => {
  return !!props.suggestionDetails.athenaProductTransaction;
});

const showTransactionInfos = computed(() => {
  return props.suggestionDetails.transaction?.amount?.formatted;
});

const showProductsInfos = computed(() => {
  return (
    props.suggestionDetails.transaction?.products?.length > 0 &&
    props.suggestionDetails.portfolio
  );
});

const showPortfolioInfos = computed(() => {
  return (
    !(props.suggestionDetails.transaction?.type?.toLowerCase() === 'product') &&
    props.suggestionDetails?.portfolio
  );
});

const toggleDynamicModal = (row: ProductInformartionWithDisclaimer) => {
  if (row) {
    dynamicContentModal.value.title = row.title;
    dynamicContentModal.value.description = row.disclaimer;
    dynamicContentModal.value.showCostsButton = !!row.action;
  }
  isDynamicContentModalOpen.value = !isDynamicContentModalOpen.value;
};

const summaryCaption = computed(() => {
  return props.isInvestment
    ? captions.investmentSummaryCaption
    : captions.redemptionSummaryCaption;
});

const portfolioCaption = computed(() => {
  if (props.suggestionDetails.portfolio?.managementType === 'Manual') return '';
  return props.isInvestment
    ? captions.investmentPortfolioCaption
    : captions.redemptionPortfolioCaption;
});

const showExpireDate = computed(() => {
  const expiredAt = props.suggestionDetails.transaction?.expiredAt;
  return expiredAt && expiredAt?.formatted && expiredAt?.formatted !== '';
});

const amountLabel = computed(() => {
  return props.isInvestment
    ? 'Valor total do investimento'
    : 'Valor total bruto';
});

const productAmountLabel = computed(() => {
  return props.isInvestment
    ? 'Valor do investimento'
    : 'Valor bruto do resgate';
});
</script>

<style lang="less" scoped>
.content-display {
  margin-bottom: @size-spacing-x600;

  :deep(.content-display__block) {
    max-width: 420px;
  }
}

.heading-display {
  margin-bottom: @size-spacing-x400;
}

.list-display {
  margin-bottom: @size-spacing-x300;

  :deep(.list-display__text) {
    max-width: 280px;
  }
}

.suggestion-details {
  &__card-title {
    margin-top: @size-spacing-x400;
  }

  &__products {
    margin-bottom: @size-spacing-x1800;
  }

  &__heading {
    display: flex;
    justify-content: space-between;
    margin-bottom: @size-spacing-x500;
    align-items: center;
  }
}
</style>
